import {
  LRS_FORM_DATA_URL,
  LRS_PREVIEW_URL,
  LRS_URL,
  MOBILE_SIGN_LINK_URL,
  MOBILE_SIGN_STATUS_URL,
} from 'constants/config';
import type { PreviousPayment } from 'models/lrs';
import { fetchJson } from 'utils/fetchJson/fetchJson';

type UploadLrsRequestBody = {
  paymentId: string;
  paymentToken: string;
  bankAccountNumber: string;
  studentFullName: string;
  studentID: string;
  transactions: PreviousPayment[];
  signatureImage: string;
};

type SaveFormDataRequestBody = {
  paymentId: string;
  paymentToken: string;
  bankAccountNumber: string;
  studentFullName: string;
  studentID: string;
  transactions: PreviousPayment[];
};

type PaymentIdentity = {
  paymentId: string;
  paymentToken: string;
};

type MobileSignLink = {
  url: string;
  expiresAt: string;
};

type SendMobileLinkPayload = PaymentIdentity & {
  phoneNumber: string;
  channel: 'whatsapp' | 'sms';
};

type PreviewFormRequestBody = {
  paymentId: string;
  paymentToken: string;
  bankAccountNumber?: string;
  studentFullName?: string;
  studentID?: string;
  transactions?: PreviousPayment[];
};

type MobileSignStatus = {
  status: 'pending' | 'opened' | 'signed' | 'timeout';
};

export const lrs = {
  fetchMobileSignLink: ({ paymentId, paymentToken }: PaymentIdentity) =>
    fetchJson<MobileSignLink>(
      `${MOBILE_SIGN_LINK_URL}?${buildPaymentQuery(paymentId, paymentToken)}`,
      { method: 'GET' },
    ),

  pollMobileSignStatus: ({ paymentId, paymentToken }: PaymentIdentity) =>
    fetchJson<MobileSignStatus>(
      `${MOBILE_SIGN_STATUS_URL}?${buildPaymentQuery(paymentId, paymentToken)}`,
      { method: 'GET' },
    ),

  sendMobileLink: (payload: SendMobileLinkPayload) => {
    const body = JSON.stringify({
      payment_id: payload.paymentId,
      payment_token: payload.paymentToken,
      phone_number: payload.phoneNumber,
      channel: payload.channel,
    });

    return fetchJson<void>(MOBILE_SIGN_LINK_URL, { method: 'POST', body });
  },

  saveFormData: (payload: SaveFormDataRequestBody) => {
    const body = JSON.stringify({
      payment_id: payload.paymentId,
      payment_token: payload.paymentToken,
      bank_account_number: payload.bankAccountNumber,
      student_full_name: payload.studentFullName,
      student_id: payload.studentID,
      transactions: parseTransactions(payload.transactions),
    });

    return fetchJson<void>(LRS_FORM_DATA_URL, { method: 'POST', body });
  },

  upload: (payload: UploadLrsRequestBody) => {
    const body = JSON.stringify({
      payment_id: payload.paymentId,
      payment_token: payload.paymentToken,
      bank_account_number: payload.bankAccountNumber,
      student_full_name: payload.studentFullName,
      student_id: payload.studentID,
      transactions: parseTransactions(payload.transactions),
      signature_image: payload.signatureImage,
    });

    return fetchJson<void>(`${LRS_URL}/upload`, { method: 'POST', body });
  },

  previewForm: (payload: PreviewFormRequestBody) => {
    const body = JSON.stringify({
      payment_id: payload.paymentId,
      payment_token: payload.paymentToken,
      bank_account_number: payload.bankAccountNumber,
      student_full_name: payload.studentFullName,
      student_id: payload.studentID,
      transactions: parseTransactions(payload.transactions),
    });

    return fetch(LRS_PREVIEW_URL, { method: 'POST', body }).then(
      async response => {
        if (!response.ok) {
          throw new Error(`LRS preview failed: ${response.status}`);
        }
        return response.arrayBuffer();
      },
    );
  },

  delete: ({
    paymentId,
    paymentToken,
  }: {
    paymentId: string;
    paymentToken: string;
  }) => {
    const options = { method: 'DELETE' };

    const url = `${LRS_URL}/${paymentId}?payment_token=${paymentToken}`;

    return fetch(url, options);
  },
};

const buildPaymentQuery = (paymentId: string, paymentToken: string) =>
  new URLSearchParams({
    payment_id: paymentId,
    payment_token: paymentToken,
  }).toString();

const parseTransactions = (transactions: PreviousPayment[] = []) => {
  if (!transactions.length) {
    return null;
  }

  return transactions.map(({ adBranch, ...transaction }) => ({
    ...transaction,
    address: adBranch,
  }));
};
